import { useCallback, useEffect, useRef, useState } from "react"
import { useClient } from "urql"

import "../graphql/entityLock.query"

import { EntityLockStatus, type UseEntityLockReturn } from "../types"
import { toEntityLock } from "../utils"

import { useAcquireEntityLock } from "./useAcquireEntityLock"
import { useHeartbeatEntityLock } from "./useHeartbeatEntityLock"
import { useReleaseEntityLock } from "./useReleaseEntityLock"

import type {
  EntityLock,
  EntityLockEntityType,
  GetEntityLockQuery,
  GetEntityLockQueryVariables,
} from "@/graphql/codegen/graphql"
import { GetEntityLockDocument } from "@/graphql/codegen/graphql"
import { useCurrentUserStore } from "@/stores/currentUserStore"

const DEFAULT_HEARTBEAT_INTERVAL_MS = 120_000

// The lock's server-side TTL (5 min) gives ~2 heartbeat cycles (2 min each) of buffer before
// it actually expires — retry transport errors (network blips, timeouts) across that buffer
// instead of giving up on the first one. A server-confirmed failure (the lock is genuinely
// gone) always skips this and flips to Lost immediately.
const MAX_CONSECUTIVE_HEARTBEAT_TRANSPORT_ERRORS = 2

interface UseEntityLockParams {
  entityType: EntityLockEntityType
  entityId: string | undefined
  enabled: boolean
  heartbeatIntervalMs?: number
}

export const useEntityLock = ({
  entityType,
  entityId,
  enabled,
  heartbeatIntervalMs = DEFAULT_HEARTBEAT_INTERVAL_MS,
}: UseEntityLockParams): UseEntityLockReturn => {
  const client = useClient()
  const currentUserId = useCurrentUserStore((state) => state.currentUser?.userId)

  const { acquireLock } = useAcquireEntityLock()
  const { heartbeat } = useHeartbeatEntityLock()
  const { release } = useReleaseEntityLock()

  const [status, setStatus] = useState<EntityLockStatus>(EntityLockStatus.Idle)
  const [holder, setHolder] = useState<EntityLock | null>(null)

  const generationRef = useRef(0)
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const heldEntityIdRef = useRef<string | null>(null)
  const consecutiveTransportErrorsRef = useRef(0)

  const clearHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current)
      heartbeatIntervalRef.current = null
    }
  }, [])

  // A heartbeat failure (or an external lock-conflict signal) means the FE gave up on the
  // lock, but the DB row may still be live and owned by us — `heldEntityIdRef` is kept intact
  // through `Lost` so a later close/cancel still releases it. `release` is idempotent
  // server-side, so retrying it against a row we no longer own is a harmless no-op.
  const markLost = useCallback(() => {
    generationRef.current += 1
    clearHeartbeat()
    setStatus(EntityLockStatus.Lost)
  }, [clearHeartbeat])

  useEffect(() => {
    if (!enabled || !entityId) {
      const previousEntityId = heldEntityIdRef.current
      generationRef.current += 1
      clearHeartbeat()
      heldEntityIdRef.current = null
      if (previousEntityId) {
        release({ entityType, entityId: previousEntityId })
      }
      setHolder(null)
      setStatus(EntityLockStatus.Idle)
      return
    }

    generationRef.current += 1
    const generation = generationRef.current
    const previousEntityId = heldEntityIdRef.current
    clearHeartbeat()
    heldEntityIdRef.current = null

    if (previousEntityId && previousEntityId !== entityId) {
      release({ entityType, entityId: previousEntityId })
    }

    setHolder(null)
    setStatus(EntityLockStatus.Acquiring)

    const startHeartbeat = () => {
      clearHeartbeat()
      consecutiveTransportErrorsRef.current = 0
      heartbeatIntervalRef.current = setInterval(async () => {
        const tickGeneration = generation
        const result = await heartbeat({ entityType, entityId })
        if (tickGeneration !== generationRef.current) return

        if (result.ok) {
          consecutiveTransportErrorsRef.current = 0
          return
        }

        // A transport error doesn't mean the lock itself was lost — retry across the TTL
        // buffer before treating it as real. A server-confirmed failure skips straight through.
        if (result.isTransportError) {
          consecutiveTransportErrorsRef.current += 1
          if (consecutiveTransportErrorsRef.current < MAX_CONSECUTIVE_HEARTBEAT_TRANSPORT_ERRORS) return
        }

        clearHeartbeat()

        // Refresh who actually holds the lock before surfacing it — the `holder` we already
        // have in state is whoever we last saw (often ourselves from the initial acquire), which
        // would otherwise show a misleading "locked by [you]" conflict banner.
        const refreshed = await client
          .query<
            GetEntityLockQuery,
            GetEntityLockQueryVariables
          >(GetEntityLockDocument, { input: { entityType, entityId } }, { requestPolicy: "network-only" })
          .toPromise()
        if (tickGeneration !== generationRef.current) return

        heldEntityIdRef.current = null
        setHolder(toEntityLock(refreshed.data?.entityLock.get) ?? null)
        setStatus(EntityLockStatus.Lost)
      }, heartbeatIntervalMs)
    }

    const run = async () => {
      const input = { entityType, entityId }
      const result = await client
        .query<
          GetEntityLockQuery,
          GetEntityLockQueryVariables
        >(GetEntityLockDocument, { input }, { requestPolicy: "network-only" })
        .toPromise()

      if (generation !== generationRef.current) return

      const liveLock = toEntityLock(result.data?.entityLock.get)

      if (liveLock && liveLock.lockedByUserId !== currentUserId) {
        heldEntityIdRef.current = null
        setHolder(liveLock)
        setStatus(EntityLockStatus.Rejected)
        return
      }

      if (liveLock && liveLock.lockedByUserId === currentUserId) {
        heldEntityIdRef.current = entityId
        setHolder(liveLock)
        setStatus(EntityLockStatus.Held)
        startHeartbeat()
        return
      }

      const acquired = await acquireLock(input)
      if (generation !== generationRef.current) return

      if (acquired.ok) {
        heldEntityIdRef.current = entityId
        setHolder(acquired.lock)
        setStatus(EntityLockStatus.Held)
        startHeartbeat()
        return
      }

      heldEntityIdRef.current = null
      setHolder(acquired.currentLock)
      setStatus(EntityLockStatus.Rejected)
    }

    run()

    return () => {
      const releaseEntityId = heldEntityIdRef.current
      generationRef.current += 1
      clearHeartbeat()
      heldEntityIdRef.current = null
      if (releaseEntityId) {
        release({ entityType, entityId: releaseEntityId })
      }
    }
  }, [
    enabled,
    entityId,
    entityType,
    heartbeatIntervalMs,
    currentUserId,
    client,
    acquireLock,
    heartbeat,
    release,
    clearHeartbeat,
  ])

  return {
    status,
    holder,
    isReadOnly: status !== EntityLockStatus.Held,
    markLost,
  }
}
